import React, { useState, useEffect } from 'react';
import { CubeIcon } from '@newcore/icons';
import { cn } from '@newcore/ui/cn';
import { Sizes } from '@newcore/ui/enums/sizes';
import type { AppIconProps, AppIconSize } from './app-icon.types';

const sizeClasses: Record<AppIconSize, string> = {
  [Sizes.SM]: 'h-6 w-6',
  [Sizes.MD]: 'h-8 w-8',
  [Sizes.LG]: 'h-12 w-12',
  [Sizes.XL]: 'h-16 w-16',
};

interface FallbackIconProps {
  appName: string;
  size: AppIconSize;
  className?: string;
}

const FallbackIcon = React.forwardRef<HTMLDivElement, FallbackIconProps>(
  ({ appName, size, className }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-center rounded-lg bg-gradient-to-br from-utility-blue-100 to-utility-blue-200 text-utility-blue-600 shadow-sm',
          sizeClasses[size],
          className
        )}
        aria-label={`${appName} icon`}
      >
        <CubeIcon className='w-2/3 h-2/3' strokeWidth={1.5} />
      </div>
    );
  }
);

FallbackIcon.displayName = 'FallbackIcon';

const AppIconImpl = React.forwardRef<
  HTMLImageElement | HTMLDivElement,
  AppIconProps
>(({ iconUrl, appName, size = Sizes.MD, className }, ref) => {
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [iconUrl]);

  if (iconUrl && !imageError) {
    return (
      <img
        ref={ref as React.Ref<HTMLImageElement>}
        src={iconUrl}
        alt={appName}
        className={cn('rounded object-cover', sizeClasses[size], className)}
        onError={() => setImageError(true)}
        decoding='async'
      />
    );
  }

  return (
    <FallbackIcon
      ref={ref as React.Ref<HTMLDivElement>}
      appName={appName}
      size={size}
      className={className}
    />
  );
});

AppIconImpl.displayName = 'AppIcon';

export const AppIcon = React.memo(AppIconImpl);
